import { inject, Injectable, isDevMode } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ConnectionService, ConnectionState } from 'connection-service';
import { disableNetwork, enableNetwork, getFirestore } from '@angular/fire/firestore';
import { Network } from '@capacitor/network';

interface WindowWithOfflineDebug {
  toggleOffline?: () => boolean;
  setOffline?: (offline: boolean) => void;
  isOfflineSimulated?: () => boolean;
}

@Injectable()
export class NetworkService {
  connected = navigator.onLine;
  status = new BehaviorSubject(this.connected);
  private offlineSimulated = false;
  private lastMonitoredConnected = this.connected;

  constructor() {
    inject(ConnectionService)
      .monitor()
      .pipe(
        filter((isConnected: ConnectionState) => isConnected.hasInternetAccess !== this.lastMonitoredConnected),
        takeUntilDestroyed(),
      )
      .subscribe(async (isConnected: ConnectionState) => {
        this.lastMonitoredConnected = isConnected.hasInternetAccess;
        if (this.offlineSimulated) {
          return;
        }
        this.applyConnected(isConnected.hasInternetAccess);
      });

    if (isDevMode()) {
      const debugWindow = globalThis as unknown as WindowWithOfflineDebug;
      debugWindow.toggleOffline = () => this.toggleOfflineSimulated();
      debugWindow.setOffline = (offline: boolean) => this.setOfflineSimulated(offline);
      debugWindow.isOfflineSimulated = () => this.isOfflineSimulated();
    }
  }

  isOfflineSimulated(): boolean {
    return this.offlineSimulated;
  }

  setOfflineSimulated(offline: boolean): void {
    this.offlineSimulated = offline;
    // Release restores the last monitored state, not navigator.onLine:
    // the link can be up (navigator.onLine true) with no internet access.
    this.applyConnected(offline ? false : this.lastMonitoredConnected);
  }

  toggleOfflineSimulated(): boolean {
    this.setOfflineSimulated(!this.offlineSimulated);
    return this.offlineSimulated;
  }

  async isAppOnline(): Promise<boolean> {
    if (this.offlineSimulated) {
      return false;
    }
    const { connected } = await Network.getStatus();
    return connected;
  }

  private applyConnected(connected: boolean): void {
    this.applyFirestoreNetwork(connected);
    this.connected = connected;
    this.status.next(connected);
  }

  private applyFirestoreNetwork(connected: boolean): void {
    try {
      if (connected) {
        enableNetwork(getFirestore()).catch(console.error);
      } else {
        disableNetwork(getFirestore()).catch(console.error);
      }
    } catch (e) {
      console.error(e);
    }
  }
}
